import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { saveVenueDraftToBackend } from './venueDraftResume';

/**
 * Wires up the two persistence actions every onboarding step needs:
 *
 *   1. `autoSave(stepNumber)` — fire-and-await on the "Continue" path so
 *      Steps 1-6 also persist to the backend (previously only Step 3 and
 *      Step 7 did this, which meant closing the tab mid-Step-2 lost
 *      everything).
 *
 *   2. `saveAndExit(stepNumber)` — explicit user-initiated save that
 *      shows a reassuring toast so they can close the tab confidently.
 *      Stays on the current page (per product call: the user closes
 *      the tab themselves; we just signal success).
 *
 * Each step still owns its own navigation logic — this hook does not
 * navigate. It only handles the network call + UX feedback.
 */
export function useOnboardingSaveActions() {
  const [savingExit, setSavingExit] = useState(false);

  const autoSave = useCallback(async (stepNumber) => {
    // Silent: failure here shouldn't block the user from moving forward
    // (they have the data in sessionStorage; we'll retry on the next
    // step's Continue or via Save & exit).
    return saveVenueDraftToBackend(stepNumber, { silent: true });
  }, []);

  const saveAndExit = useCallback(async (stepNumber) => {
    if (savingExit) return;
    setSavingExit(true);
    const result = await saveVenueDraftToBackend(stepNumber);
    setSavingExit(false);
    if (result.ok) {
      toast.success('Saved! Pick up anytime from the Allure app.', { duration: 4500 });
    }
  }, [savingExit]);

  return { autoSave, saveAndExit, savingExit };
}
